/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/QaNewChecklistDraftTable.tsx
 * FILE_ID: JOBHUB-QA-NEWCL-DRAFT-TABLE-001
 * PURPOSE: Table layout for new-checklist draft rows (desktop default, phone toggle).
 * LANDMINE: in `fill` the Item column is sticky, so its header cell is sticky on
 *      BOTH axes and sits above the other headers; body cells stay below the header.
 */
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { EditableCell } from '../../../qa/EditableCell'
import type { QaDraftRow } from './useQaNewChecklistDraft'

export function QaNewChecklistDraftTable({
  rows,
  onPatch,
  onMove,
  onRemove,
  fill = false,
}: {
  rows: QaDraftRow[]
  onPatch: (key: string, fields: Partial<QaDraftRow>) => void
  onMove: (index: number, direction: -1 | 1) => void
  onRemove: (key: string) => void
  /** Phone sheet: fill the scroller, keep the header row and Item column in view. */
  fill?: boolean
}) {
  const th = cn('px-1 py-1', fill && 'sticky top-0 z-[2] bg-card')
  return (
    <table className={cn('w-full border-collapse text-left text-xs', fill && 'min-w-[640px]')}>
      <thead>
        <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
          <th className={cn(th, fill && 'left-0 z-[3] min-w-[140px]')}>Item</th>
          <th className={th}>Product</th>
          <th className={th}>Requirement</th>
          <th className={th}>Acceptance</th>
          <th className={cn(th, 'text-center')}>Hold</th>
          <th className={th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.key} className="align-top">
            <td className={cn('px-1 py-1', fill && 'sticky left-0 z-[1] min-w-[140px] bg-card')}>
              <EditableCell
                value={row.title}
                singleLine
                allowEmpty
                ariaLabel={`Draft row ${index + 1} title`}
                placeholder="Check name"
                onCommit={(next) => onPatch(row.key, { title: next })}
              />
            </td>
            <td className="px-1 py-1">
              <EditableCell
                value={row.product}
                singleLine
                ariaLabel={`Draft row ${index + 1} product`}
                placeholder="Product / material"
                onCommit={(next) => onPatch(row.key, { product: next })}
              />
            </td>
            <td className="px-1 py-1">
              <EditableCell
                value={row.requirement_text}
                ariaLabel={`Draft row ${index + 1} requirement`}
                placeholder="What to verify"
                onCommit={(next) => onPatch(row.key, { requirement_text: next })}
              />
            </td>
            <td className="px-1 py-1">
              <EditableCell
                value={row.acceptance_criteria}
                ariaLabel={`Draft row ${index + 1} acceptance criteria`}
                placeholder="Pass condition"
                onCommit={(next) => onPatch(row.key, { acceptance_criteria: next })}
              />
            </td>
            <td className="px-1 py-1 text-center">
              <input
                type="checkbox"
                checked={row.has_hold_point}
                aria-label={`Hold point for draft row ${index + 1}`}
                onChange={(event) => onPatch(row.key, { has_hold_point: event.target.checked })}
              />
            </td>
            <td className="px-1 py-1">
              <div className="flex items-center gap-0.5">
                <button type="button" aria-label="Move draft row up" disabled={index === 0} onClick={() => onMove(index, -1)}>
                  <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button type="button" aria-label="Move draft row down" disabled={index === rows.length - 1} onClick={() => onMove(index, 1)}>
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="Remove draft row"
                  onClick={() => onRemove(row.key)}
                >
                  <X className="h-3.5 w-3.5 text-destructive" aria-hidden />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
